import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  CaixaDeRecorte,
  TamanhoDestino,
  TIPOS_DE_IMAGEM_ACEITOS,
  recortarEReduzirImagem,
  tipoDeImagemAceito,
} from './recorteDeImagem';
import useCropBox from './useCropBox';
import FileService from '../../../../sdk/services/FileService';

export interface AlvoDeImagemDaFaceProps {
  /** Chamado uma vez, com a chave do arquivo já enviado, quando o envio termina
   *  com sucesso. Quem usa este componente sabe em qual campo — frente ou
   *  verso — inserir o token `/files/<chave>`; este componente não conhece a
   *  ideia de "campo", só entrega a chave. */
  onImagemEnviada: (fileKey: string) => void;
  /** Tamanho de saída pedido, em px. Sem proporção fixa, sem rotação — decisões de
   *  produto que ninguém tomou ainda. Só encolhe um recorte grande, nunca amplia
   *  (`recortarEReduzirImagem` já garante isso). */
  destino?: TamanhoDestino;
  className?: string;
  /** Override do envio — default `FileService.upload(file, 'review_image')`,
   *  o mesmo padrão de `OcclusionEditor`'s `uploadImage` (serve para Storybook
   *  e testes, sem rede de verdade). */
  uploadImage?: (file: File) => Promise<string>;
}

export interface AlvoDeImagemDaFaceHandle {
  /** Abre o seletor de arquivo deste alvo. Não altera o texto de nenhum campo. */
  abrir: () => void;
}

const DESTINO_PADRAO: TamanhoDestino = { width: 1024, height: 1024 };

const defaultUploadImage = (file: File) =>
  FileService.upload(file, 'review_image').then((r) => r.file_key);

/** Três fases, nunca um booleano: "recortando" e "enviando" guardam fases
 *  diferentes do mesmo `handleConfirmar`, e um só `status !== 'ocioso'`
 *  (`processando`) basta para desabilitar os dois botões nas duas. */
type StatusDeEnvio = 'ocioso' | 'recortando' | 'enviando';

/** `accept` do input nativo, construído a partir da MESMA lista que valida o
 *  arquivo recebido — nunca uma segunda cópia capaz de divergir dela. */
const ACCEPT = TIPOS_DE_IMAGEM_ACEITOS.join(',');

/** Posição (% da caixa) e cursor das 6 alças. Espelham, uma a uma, as mesmas
 *  6 zonas de hit-test que `useCropBox` já calcula por conta própria — o que
 *  o admin vê aqui precisa bater com o que ele consegue agarrar lá. */
const ALCAS: ReadonlyArray<{ pos: string; left: string; top: string; cursor: string }> = [
  { pos: 'nw', left: '0%', top: '0%', cursor: 'nwse-resize' },
  { pos: 'ne', left: '100%', top: '0%', cursor: 'nesw-resize' },
  { pos: 'sw', left: '0%', top: '100%', cursor: 'nesw-resize' },
  { pos: 'se', left: '100%', top: '100%', cursor: 'nwse-resize' },
  { pos: 'e', left: '100%', top: '50%', cursor: 'ew-resize' },
  { pos: 's', left: '50%', top: '100%', cursor: 'ns-resize' },
];

function cx(...partes: Array<string | false | null | undefined>): string {
  return partes.filter(Boolean).join(' ');
}

/**
 * Alvo de arquivo para a face "Imagem" do compositor de cartão (frente/verso
 * em texto) — irmão do passo de upload de imagem do `OcclusionEditor`, mas
 * para este ladrilho, não para a oclusão (aquele componente não é tocado
 * aqui). O admin escolhe uma imagem (clique, arraste ou colagem), recorta e
 * encolhe com uma caixa arrastável/redimensionável, vê uma prévia ao vivo do
 * resultado e, ao confirmar, recorta de novo, envia via `uploadImage`
 * (default `FileService.upload(file, 'review_image')`) e entrega a chave
 * resultante via `onImagemEnviada`. Uma falha em qualquer uma das duas fases
 * mantém a imagem escolhida disponível para nova tentativa.
 */
const AlvoDeImagemDaFace = forwardRef<
  AlvoDeImagemDaFaceHandle,
  AlvoDeImagemDaFaceProps
>(function AlvoDeImagemDaFace(
  {
    onImagemEnviada,
    destino = DESTINO_PADRAO,
    className,
    uploadImage = defaultUploadImage,
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropzoneRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  /** Trava síncrona de `handleConfirmar`. `processando` só fica verdadeiro no
   *  render seguinte, então dois cliques antes disso veriam os dois
   *  `processando === false` e os dois enviariam. */
  const emCurso = useRef(false);
  const onImagemEnviadaRef = useRef(onImagemEnviada);
  onImagemEnviadaRef.current = onImagemEnviada;

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [urlOriginal, setUrlOriginal] = useState<string | null>(null);
  const [box, setBox] = useState<CaixaDeRecorte | null>(null);
  const [arrastandoSobre, setArrastandoSobre] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusDeEnvio>('ocioso');
  const processando = status !== 'ocioso';
  const [urlPreview, setUrlPreview] = useState<string | null>(null);
  const [gerandoPreview, setGerandoPreview] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      abrir() {
        if (processando) return;
        inputRef.current?.click();
      },
    }),
    [processando],
  );

  // Hooks não podem ser condicionais: chamado sempre, mesmo no ramo vazio, onde
  // `imageRef.current` ainda é null e o hook fica inofensivo (nunca recebe um
  // pointerdown, pois o overlay que o dispara só existe no ramo de recorte).
  // `draft` (modo de primeiro-traço) nunca é lido: `box` já nasce preenchido
  // no mesmo `receberArquivo` que grava o arquivo, então esse ramo do hook é
  // inalcançável aqui por construção.
  const { handlePointerDown } = useCropBox({ box, imageRef, onChange: setBox });

  // Único lugar que cria/revoga a URL do arquivo original.
  useEffect(() => {
    if (!arquivo) {
      setUrlOriginal(null);
      return;
    }
    const url = URL.createObjectURL(arquivo);
    setUrlOriginal(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  // Sem arquivo ainda: foca a zona de arrastar/colar, para que colar funcione
  // sem precisar de um clique antes — inclusive quando este componente abre
  // dentro de um modal/popover.
  useEffect(() => {
    if (!arquivo) dropzoneRef.current?.focus();
  }, [arquivo]);

  // Prévia do recorte, sempre a partir do arquivo ORIGINAL — nunca reencoda uma
  // prévia já reduzida, para não acumular perda de qualidade a cada ajuste da
  // caixa. Debounced (~150ms) e com uma flag `cancelado` por rodada, para que
  // o resultado de uma rodada anterior nunca sobrescreva o de uma mais nova —
  // único lugar que cria/revoga a URL da prévia.
  useEffect(() => {
    if (!arquivo || !box) {
      setUrlPreview(null);
      setGerandoPreview(false);
      return;
    }

    let cancelado = false;
    let urlGerada: string | null = null;
    setGerandoPreview(true);

    const timer = setTimeout(() => {
      recortarEReduzirImagem(arquivo, box, destino)
        .then((resultado) => {
          if (cancelado) return;
          const url = URL.createObjectURL(resultado);
          urlGerada = url;
          setUrlPreview(url);
        })
        .catch(() => {
          // Falha ao gerar a prévia não é a mesma coisa que falhar ao
          // confirmar (ver handleConfirmar) — aqui só paramos de mostrar
          // "Gerando...". Se nunca houve uma prévia anterior, o placeholder
          // cai para "Ajuste a área de recorte".
        })
        .finally(() => {
          if (!cancelado) setGerandoPreview(false);
        });
    }, 150);

    return () => {
      cancelado = true;
      clearTimeout(timer);
      if (urlGerada) URL.revokeObjectURL(urlGerada);
    };
  }, [arquivo, box, destino]);

  /** Único funil para os três caminhos de entrada (clique, arraste, colagem).
   *  Um tipo rejeitado nunca toca `arquivo`/`box` — o componente continua no
   *  ramo vazio, com o erro mostrado abaixo da zona de soltar. */
  const receberArquivo = (file: File | null | undefined) => {
    if (!file) return;
    if (!tipoDeImagemAceito(file.type)) {
      setErro(`Tipo de imagem não aceito: "${file.type || 'desconhecido'}". Envie PNG ou JPEG.`);
      return;
    }
    setErro(null);
    setArquivo(file);
    setBox({ x: 0, y: 0, w: 100, h: 100 });
  };

  const handleTrocarImagem = () => {
    if (processando || emCurso.current) return;
    setArquivo(null);
    setBox(null);
    setErro(null);
  };

  /** Recorta/reduz de novo, deliberadamente — nunca reaproveita o arquivo da
   *  prévia debounced, para a confirmação nunca depender do timing do debounce.
   *  `emCurso` é checado e armado antes de qualquer await: um segundo chamado
   *  no mesmo alvo volta imediatamente, sem recortar nem enviar de novo.
   *  `processando` continua barrando o clique depois do render. Falha em
   *  qualquer fase libera o ref e mantém `arquivo`/`box` intactos — a imagem
   *  escolhida segue disponível para nova tentativa, e nenhum endereço é
   *  entregue ao chamador. O callback lido é o mais recente (`onImagemEnviadaRef`),
   *  e só depois que o envio resolve. */
  const handleConfirmar = async () => {
    if (emCurso.current) return;
    emCurso.current = true;
    try {
      if (!arquivo || !box || processando) return;
      setErro(null);
      setStatus('recortando');
      let recortado: File;
      try {
        recortado = await recortarEReduzirImagem(arquivo, box, destino);
      } catch (e) {
        setErro(e instanceof Error ? e.message : 'Falha ao recortar a imagem.');
        setStatus('ocioso');
        return;
      }

      setStatus('enviando');
      try {
        const fileKey = await uploadImage(recortado);
        onImagemEnviadaRef.current(fileKey);
        // Pronto para a próxima imagem — o token já foi entregue a quem chamou.
        setArquivo(null);
        setBox(null);
        setStatus('ocioso');
      } catch {
        setErro('Falha ao enviar a imagem. Tente novamente.');
        setStatus('ocioso');
      }
    } finally {
      emCurso.current = false;
    }
  };

  const seletorDeArquivo = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      tabIndex={-1}
      aria-hidden="true"
      className="sr-only"
      onChange={(event) => {
        receberArquivo(event.target.files?.[0]);
        event.target.value = '';
      }}
    />
  );

  if (!arquivo) {
    return (
      <div className={cx('flex flex-col gap-2', className)}>
        {seletorDeArquivo}
        <div
          ref={dropzoneRef}
          role="button"
          tabIndex={0}
          aria-label="Escolher imagem"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setArrastandoSobre(true);
          }}
          onDragLeave={() => setArrastandoSobre(false)}
          onDrop={(event) => {
            event.preventDefault();
            setArrastandoSobre(false);
            receberArquivo(event.dataTransfer.files?.[0]);
          }}
          onPaste={(event) => {
            const items = event.clipboardData.items;
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.type.startsWith('image/')) {
                receberArquivo(item.getAsFile());
                break;
              }
            }
          }}
          className={cx(
            'flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed px-6 py-10 text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-select/60',
            arrastandoSobre
              ? 'border-select bg-select/[0.08]'
              : 'border-edge bg-surface hover:bg-surface-hover',
          )}
        >
          <p className="text-sm font-medium text-ink">Arraste ou cole uma imagem (opcional)</p>
          <p className="text-xs text-ink-muted">Aceita PNG ou JPEG.</p>
          <span className="mt-2 inline-flex items-center rounded-chip border border-edge-strong bg-surface px-3 py-1.5 text-sm font-medium text-ink">
            Selecionar arquivo
          </span>
        </div>
        {erro && (
          <p role="alert" className="text-[13px] text-danger-ink">
            {erro}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cx('flex flex-col gap-4 lg:flex-row lg:items-start', className)}>
      {seletorDeArquivo}
      <div className="flex w-full min-w-0 flex-1 flex-col gap-3">
        <div className="relative w-full min-w-0 overflow-hidden rounded-card border border-edge bg-surface">
          <img
            ref={imageRef}
            src={urlOriginal ?? undefined}
            alt="Imagem selecionada para recorte"
            draggable={false}
            className="block w-full"
          />
          <div
            className="absolute inset-0"
            style={{ touchAction: 'none' }}
            onPointerDown={handlePointerDown}
          >
            {box && (
              <div
                className="absolute cursor-move border-2 border-select bg-select/[0.14]"
                style={{
                  left: `${box.x}%`,
                  top: `${box.y}%`,
                  width: `${box.w}%`,
                  height: `${box.h}%`,
                }}
              >
                {ALCAS.map((alca) => (
                  <span
                    key={alca.pos}
                    className="absolute h-3 w-3 rounded-full border border-select bg-surface"
                    style={{
                      left: alca.left,
                      top: alca.top,
                      transform: 'translate(-50%,-50%)',
                      cursor: alca.cursor,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleTrocarImagem}
            disabled={processando}
            className="inline-flex items-center rounded-chip border border-edge bg-surface px-3.5 py-2 text-sm font-medium text-ink hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Trocar imagem
          </button>
          <button
            type="button"
            onClick={handleConfirmar}
            disabled={!box || processando}
            className="inline-flex items-center rounded-chip bg-brand px-3.5 py-2 text-sm font-semibold text-brand-contrast disabled:cursor-not-allowed disabled:opacity-50"
          >
            {status === 'recortando' ? 'Recortando…' : status === 'enviando' ? 'Enviando…' : 'Usar esta imagem'}
          </button>
        </div>

        {processando && (
          <p role="status" className="text-[13px] text-ink-muted">
            {status === 'recortando' ? 'Recortando a imagem…' : 'Enviando a imagem…'}
          </p>
        )}

        {erro && (
          <p role="alert" className="text-[13px] text-danger-ink">
            {erro}
          </p>
        )}
      </div>

      <div className="flex w-full flex-col gap-2 lg:w-64 lg:shrink-0">
        <span className="text-sm font-medium text-ink">Pré-visualização</span>
        <div className="aspect-square w-full overflow-hidden rounded-card border border-edge bg-surface">
          {urlPreview ? (
            <img
              src={urlPreview}
              className="h-full w-full object-contain"
              alt="Pré-visualização do recorte"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center p-3 text-center text-xs text-ink-muted">
              {gerandoPreview ? 'Gerando pré-visualização…' : 'Ajuste a área de recorte'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default AlvoDeImagemDaFace;
